import "./tokens.css";
import "./global.css";

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "react-query";
import { RouterProvider } from "react-router-dom";

import { InrangeAgentsApiConfigProvider } from "./api";
import { makeQueryClient } from "./api/queryClient";
import {
  KindeAuthenticatedUI,
  KindeRedirectProvider,
  UserProvider,
} from "./auth";
import { router } from "./AppRoutes";
import { MutationErrorToast } from "./components/MutationErrorToast";
import { stopIosFocusZoom } from "./utils/stopIosFocusZoom";

stopIosFocusZoom();

// API base URL is inlined at build time from VITE_AGENTS_API_HOSTNAME.
// CI sets it per env:
//   - production: https://api.agents.inrange.io
//   - QA (per-PR): https://api.<branch>.agents.inrange.dev
//   - personal dev: https://api.<env>.dev-<name>.agents.inrange.dev
// Local dev (`yarn dev`) falls back to localhost:8000; override via
// `VITE_AGENTS_API_HOSTNAME=… yarn dev` to point at a deployed env.
// A production build (`yarn build`) without VITE_AGENTS_API_HOSTNAME
// fails fast - silently shipping a bundle that points at localhost
// produces mysterious CORS errors instead of an obvious build error.
const AGENTS_API_BASE_URL: string = (() => {
  const fromEnv = import.meta.env.VITE_AGENTS_API_HOSTNAME;
  if (fromEnv) return fromEnv;
  if (import.meta.env.DEV) return "http://localhost:8000";
  throw new Error(
    "VITE_AGENTS_API_HOSTNAME must be set at build time for production " +
      "builds. Set it before `yarn build` (see commands/deploy-frontend.sh " +
      "or .github/workflows/qa-env-create.yml for examples).",
  );
})();

// A deploy replaces the hashed JS chunks, so a tab open across a deploy can
// hold an index/bundle that references a chunk hash that no longer exists; a
// lazy import (e.g. the /costs ECharts chunk) then throws "Failed to fetch
// dynamically imported module" and React renders the white "Unexpected
// Application Error" screen. Vite fires `vite:preloadError` in that case -
// reload once to pick up the fresh manifest. The sessionStorage guard stops a
// genuinely-missing asset from looping the page.
window.addEventListener("vite:preloadError", () => {
  if (sessionStorage.getItem("preloadErrorReloaded") === "1") return;
  sessionStorage.setItem("preloadErrorReloaded", "1");
  window.location.reload();
});

const queryClient = makeQueryClient();

// Provider stack, outermost first. KindeRedirectProvider must wrap
// KindeAuthenticatedUI so the onRedirectCallback can write into it.
// UserProvider sits inside KindeAuthenticatedUI so it can call
// useKindeAuth() and gate rendering until the user resolves.
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <KindeRedirectProvider>
      <KindeAuthenticatedUI>
        <UserProvider>
          <InrangeAgentsApiConfigProvider
            config={{ baseUrl: AGENTS_API_BASE_URL }}
          >
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <MutationErrorToast />
            </QueryClientProvider>
          </InrangeAgentsApiConfigProvider>
        </UserProvider>
      </KindeAuthenticatedUI>
    </KindeRedirectProvider>
  </React.StrictMode>,
);

// The app mounted, so any earlier preload-error reload succeeded - clear the
// guard so a future deploy's stale chunk gets its own one-shot reload.
sessionStorage.removeItem("preloadErrorReloaded");
